// Page-by-page notes (owner feedback 2026-10-09): one NCERT topic/subtopic per page with
// Previous / Next, designed for visual learning. Pieces used by pages/app/AppNotes.js.
//
// Rule kept everywhere: the words inside a point ([data-pt]) are never changed and no
// block tag (div/p/li…) is added inside it, so highlight offsets (components/app/marks.js)
// mean the same letters as on the server. Flow chips and arrows are <span>s only.
import React from 'react';
import { C, FONT } from './theme';

// ------------------------------------------------------------------ styles of a page

export const PAGE_CSS = `
.v2-page .v2-arr{display:inline-flex;align-items:center;color:${C.primary};font-weight:800;font-size:1.15em;padding:0 3px}
.v2-page .v2-flow{display:inline;line-height:2.1}
.v2-page .v2-chip{display:inline;padding:3px 10px;margin:0 1px;border-radius:999px;background:#EEF2FF;
  border:1px solid #C7D2FE;-webkit-box-decoration-break:clone;box-decoration-break:clone}
.v2-page .v2-flow .v2-arr{padding:0 6px;font-size:1.1em}
.v2-page table{border-collapse:separate;border-spacing:0;margin:8px 0;font-size:15.5px;line-height:1.4;
  border:1px solid #C7D2FE;border-radius:10px;overflow:hidden;min-width:60%}
.v2-page th{background:#4F46E5;color:#FFFFFF;font-weight:700;padding:8px 12px;text-align:left;border:0}
.v2-page th .c-red,.v2-page th .c-blue,.v2-page th .c-green{color:#FFFFFF}
.v2-page td{padding:7px 12px;border:0;border-top:1px solid #E0E7FF;vertical-align:top;text-align:left}
.v2-page tr:nth-child(even) td{background:#F5F7FF}
.v2-page .v2-card{transition:box-shadow .15s ease, transform .15s ease}
.v2-page .v2-card:hover{box-shadow:0 6px 18px rgba(30,27,75,.08)}
.v2-pager-btn:hover{border-color:#A5B4FC;box-shadow:0 8px 22px rgba(79,70,229,.14)}
.v2-pager-btn:focus-visible{outline:3px solid #A5B4FC;outline-offset:2px}
@keyframes v2pagein{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.v2-page{animation:v2pagein .28s ease-out}
@keyframes v2itemin{from{opacity:0;transform:translateY(14px) scale(.985)}to{opacity:1;transform:none}}
.v2-reveal{animation:v2itemin .55s cubic-bezier(.2,.7,.2,1)}
.v2-outline-done{color:#047857}
`;

// ------------------------------------------------------------------ html helpers

// True when every tag opened in `part` is closed in it (so it can stand alone in a chip).
function balanced(part) {
  const stack = [];
  const re = /<\/?([a-zA-Z0-9]+)[^>]*?(\/?)>/g;
  let m;
  while ((m = re.exec(part))) {
    const name = m[1].toLowerCase();
    if (name === 'br' || m[2] === '/') continue;
    if (m[0][1] === '/') {
      if (stack.pop() !== name) return false;
    } else {
      stack.push(name);
    }
  }
  return stack.length === 0;
}

// "A → B → C" becomes chips joined by arrows when it is a short sequence; any other "→"
// is drawn as a coloured arrow. The text content stays exactly the same.
export function decorateHtml(html) {
  if (!html || html.indexOf('→') < 0 || html.indexOf('<table') >= 0) return html;
  const parts = html.split('→');
  const plainLen = html.replace(/<[^>]+>/g, '').length;
  if (parts.length >= 3 && plainLen <= 240 && parts.every(balanced)
      && parts.every((p) => p.replace(/<[^>]+>/g, '').trim().length > 0)) {
    return `<span class="v2-flow">${parts.map((p) => `<span class="v2-chip">${p}</span>`)
      .join('<span class="v2-arr">→</span>')}</span>`;
  }
  return parts.join('<span class="v2-arr">→</span>');
}

function textOf(html) {
  const box = document.createElement('div');
  box.innerHTML = html;
  return (box.textContent || '').replace(/\s+/g, ' ').trim();
}

// What to show in the "Remember" card at the end of a page: the classic exam lines
// (c-mark) and the numbers marked must-memorise (c-red with a digit) of the unit.
export function recapOf(points) {
  const lines = [];
  const numbers = [];
  const seenL = new Set();
  const seenN = new Set();
  points.forEach((p) => {
    const box = document.createElement('div');
    box.innerHTML = p.html || '';
    if (box.querySelector('.c-mark') && !box.querySelector('table')) {
      let t = (box.textContent || '').replace(/\s+/g, ' ').trim();
      if (t.length > 170) t = `${t.slice(0, 169)}…`;
      if (t.length >= 8 && !seenL.has(t)) { seenL.add(t); lines.push({ text: t, code: p.code }); }
    }
    box.querySelectorAll('.c-red').forEach((el) => {
      const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (/\d/.test(t) && t.length <= 40 && !seenN.has(t)) { seenN.add(t); numbers.push({ text: t, code: p.code }); }
    });
  });
  return { lines: lines.slice(0, 8), numbers: numbers.slice(0, 12) };
}

export function hasExamLine(html) {
  return (html || '').indexOf('c-mark') >= 0;
}

export function plainShort(html, n = 70) {
  const t = textOf(html || '');
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

// ------------------------------------------------------------------ small visual pieces

const IconStar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3 6.6 7 .7-5.3 4.8 1.6 7L12 17.6 5.7 21.1l1.6-7L2 9.3l7-.7z" /></svg>
);
const IconArrowL = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
);
const IconArrowR = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
);
const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
);
export { IconCheck };

export function ExamTag() {
  return (
    <span title="Classic exam line" style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 8px', height: 22,
      borderRadius: 11, background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D',
      fontFamily: FONT.ui, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
    }}><IconStar />Exam line</span>
  );
}

export function BeyondTag() {
  return (
    <span title="Not in NCERT — a well-known NEET fact" style={{
      display: 'inline-flex', alignItems: 'center', padding: '0 8px', height: 22, borderRadius: 11,
      background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0',
      fontFamily: FONT.ui, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
    }}>Beyond NCERT</span>
  );
}

// Colour key (owner 2026-10-31: moved out of the page header into the outline column,
// so the header stays slim and the reading space is free).
export function ColourKey({ light = false }) {
  const item = (bg, color, label) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: bg, border: color ? `2px solid ${color}` : 0 }} />
      {label}
    </span>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: light ? '6px 14px' : 16, fontFamily: FONT.ui, fontSize: 12.5, color: light ? C.muted : 'rgba(255,255,255,.86)' }}>
      {item('#FCA5A5', null, 'Red: must memorise')}
      {item('#93C5FD', null, 'Blue: names & terms')}
      {item('#86EFAC', null, 'Green: examples')}
      {item(C.marker, null, 'Yellow: exam line')}
    </div>
  );
}

// The slim header of a page (owner 2026-10-31: the old coloured band wasted reading space —
// now one white card: breadcrumb + page count, number + title + the actions as icons, one
// muted line of facts, and a 3 px progress line). The colour key lives in the outline.
export function PageHero({ unit, parentLabel, chapterTitle, pageNo, pageCount, stats, children }) {
  const special = unit.kind === 'summary' ? 'Summary' : unit.kind === 'exercises' ? 'Exercises'
    : unit.kind === 'intro' ? 'Start' : null;
  const accent = unit.kind === 'exercises' ? '#0F766E' : unit.kind === 'summary' ? '#B45309' : C.primary;
  const facts = [];
  if (unit.page != null) facts.push(`NCERT p. ${unit.page}`);
  if (unit.kind === 'misc') facts.push('Extra · not in NCERT');
  facts.push(...stats);
  return (
    <header data-testid="v2-page-hero" style={{
      position: 'relative', overflow: 'hidden', borderRadius: 14, padding: '12px 18px 12px',
      background: C.surface, border: `1px solid ${C.border}`, boxShadow: '0 2px 10px rgba(30,27,75,.06)',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontFamily: FONT.ui, fontSize: 12.5, color: C.muted }}>
        <span>{chapterTitle}</span>
        {parentLabel && <><span aria-hidden="true">›</span><span>{parentLabel}</span></>}
        <span style={{ flexGrow: 1 }} />
        <span data-testid="v2-page-count" style={{ fontWeight: 600, color: C.muted }}>Page {pageNo} of {pageCount}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', position: 'relative' }}>
        {(unit.number || special) && (
          <div style={{
            fontFamily: FONT.heading, fontWeight: 700, fontSize: unit.number ? 22 : 15, lineHeight: 1,
            letterSpacing: '-0.01em', color: accent, padding: '7px 10px', borderRadius: 10,
            background: unit.kind === 'exercises' ? '#ECFDF5' : unit.kind === 'summary' ? '#FFFBEB' : C.tint,
            whiteSpace: 'nowrap',
          }}>{unit.number || special}</div>
        )}
        <h2 style={{
          margin: 0, flex: '1 1 260px', minWidth: 0, fontFamily: FONT.heading, fontWeight: 700, fontSize: 24, lineHeight: 1.2, color: C.text,
          fontStyle: unit.kind === 'heading' ? 'italic' : 'normal',
        }}>{unit.title}</h2>
        {children}
      </div>
      {facts.length > 0 && (
        <div style={{ fontFamily: FONT.ui, fontSize: 13, color: C.muted }}>{facts.join(' · ')}</div>
      )}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: '#EEF2FF' }}
        role="progressbar" aria-valuemin={1} aria-valuemax={pageCount} aria-valuenow={pageNo} aria-label="Pages read in this chapter">
        <div style={{ width: `${(pageNo / pageCount) * 100}%`, height: '100%', background: accent }} />
      </div>
    </header>
  );
}

// "Remember" card at the end of a page.
export function RecapCard({ recap, onJump }) {
  if (!recap.lines.length && !recap.numbers.length) return null;
  return (
    <section data-testid="v2-recap" aria-label="Remember from this page" style={{
      marginTop: 10, borderRadius: 18, padding: '20px 24px', background: '#FFFBEB',
      border: '1px solid #FCD34D', boxShadow: '0 4px 14px rgba(180,83,9,.08)',
      display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT.notes,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 34, height: 34, borderRadius: 10, background: '#F59E0B', color: '#FFFFFF',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><IconStar /></span>
        <div style={{ fontFamily: FONT.heading, fontWeight: 700, fontSize: 19, color: '#78350F' }}>Remember from this page</div>
      </div>
      {recap.lines.length > 0 && (
        <ol style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 16.5, lineHeight: 1.5, color: '#451A03' }}>
          {recap.lines.map((l) => (
            <li key={l.code + l.text}>
              <button type="button" onClick={() => onJump(l.code)} title="Show this point" style={{
                display: 'inline', padding: 0, border: 0, background: 'transparent', textAlign: 'left', cursor: 'pointer',
                font: 'inherit', color: 'inherit', verticalAlign: 'baseline',
              }}>{l.text}</button>
            </li>
          ))}
        </ol>
      )}
      {recap.numbers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontFamily: FONT.ui, fontSize: 12.5, fontWeight: 700, color: '#92400E', letterSpacing: '.04em', textTransform: 'uppercase' }}>Numbers to know</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {recap.numbers.map((n) => (
              <button key={n.code + n.text} type="button" onClick={() => onJump(n.code)} title="Show this point" style={{
                minHeight: 34, padding: '4px 12px', borderRadius: 999, border: '1px solid #FCA5A5',
                background: '#FFFFFF', color: C.noteRed, fontFamily: FONT.ui, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}>{n.text}</button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// Previous / Next. `compact` = the small pair at the top of the page.
export function Pager({ prev, next, onGo, compact = false, labelOf, pageNo, pageCount, onFinish }) {
  if (compact) {
    // owner 2026-10-31: icon-only arrows in the slim header
    const b = (u, dir) => (
      <button type="button" className="v2-pager-btn" disabled={!u} onClick={() => u && onGo(u.code)}
        data-testid={dir === 'prev' ? 'v2-page-prev-top' : 'v2-page-next-top'}
        title={u ? `${dir === 'prev' ? 'Previous' : 'Next'}: ${labelOf(u)}` : undefined}
        aria-label={u ? `${dir === 'prev' ? 'Previous' : 'Next'}: ${labelOf(u)}` : (dir === 'prev' ? 'No previous page' : 'No next page')}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 10,
          border: `1px solid ${C.border}`, background: C.surface, color: u ? C.text : '#CBD5E1',
          cursor: u ? 'pointer' : 'default', padding: 0,
        }}>
        {dir === 'prev' ? <IconArrowL /> : <IconArrowR />}
      </button>
    );
    return <div style={{ display: 'flex', gap: 6 }}>{b(prev, 'prev')}{b(next, 'next')}</div>;
  }
  const card = (u, dir) => {
    const isNext = dir === 'next';
    if (!u) {
      if (!isNext) return <div style={{ flex: '1 1 260px' }} />;
      return (
        <button type="button" className="v2-pager-btn" data-testid="v2-page-finish" onClick={onFinish} style={{
          ...pagerCard, justifyContent: 'center', background: 'linear-gradient(135deg,#047857,#065F46)', color: '#FFFFFF', border: 0,
        }}>
          <IconCheck />
          <span style={{ fontFamily: FONT.heading, fontSize: 18, fontWeight: 700 }}>Chapter finished · See all chapters</span>
        </button>
      );
    }
    return (
      <button type="button" className="v2-pager-btn" onClick={() => onGo(u.code)}
        data-testid={isNext ? 'v2-page-next' : 'v2-page-prev'} style={{
          ...pagerCard, flexDirection: isNext ? 'row-reverse' : 'row', textAlign: isNext ? 'right' : 'left',
          background: isNext ? C.primary : C.surface, color: isNext ? '#FFFFFF' : C.text,
          border: isNext ? 0 : `1px solid ${C.border}`,
        }}>
        <span style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: isNext ? 'rgba(255,255,255,.16)' : C.tint, color: isNext ? '#FFFFFF' : C.primary,
        }}>{isNext ? <IconArrowR /> : <IconArrowL />}</span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontFamily: FONT.ui, fontSize: 12.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', opacity: 0.8 }}>
            {isNext ? 'Next' : 'Previous'}
          </span>
          <span style={{ fontFamily: FONT.heading, fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>{labelOf(u)}</span>
        </span>
      </button>
    );
  };
  return (
    <nav aria-label="Pages" data-testid="v2-pager" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        {card(prev, 'prev')}
        {card(next, 'next')}
      </div>
      <div style={{ textAlign: 'center', fontFamily: FONT.ui, fontSize: 13, color: C.muted }}>
        Page {pageNo} of {pageCount} · You can also use the ← and → keys
      </div>
    </nav>
  );
}
const pagerCard = {
  flex: '1 1 260px', minHeight: 76, display: 'flex', alignItems: 'center', gap: 14,
  padding: '14px 18px', borderRadius: 16, cursor: 'pointer', boxSizing: 'border-box',
  boxShadow: '0 4px 14px rgba(15,23,42,.06)', transition: 'box-shadow .15s ease, border-color .15s ease',
};
